import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Button, Paper, Grid, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Chip, IconButton, Collapse,
    Alert, CircularProgress, TextField, Tabs, Tab, Dialog, DialogTitle,
    DialogContent, DialogActions, Checkbox, FormControlLabel, Divider,
    Card, CardContent, CardActions, Switch, FormGroup, Stack, Tooltip,
    TablePagination, Select, MenuItem, FormControl, InputLabel, Accordion, AccordionSummary, AccordionDetails,
    OutlinedInput, ListItemText, ToggleButton, ToggleButtonGroup
} from '@mui/material';
import {
    Upload as UploadIcon, ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon,
    CheckCircle as CheckCircleIcon, Error as ErrorIcon, Info as InfoIcon,
    Add as AddIcon, Edit as EditIcon, PlayArrow as PlayArrowIcon,
    History as HistoryIcon, CloudUpload as CloudUploadIcon, FilterList as FilterListIcon,
    Image as ImageIcon, Map as MapIcon, Add as AddIcon2, Remove as RemoveIcon, Description as DescriptionIcon,
    Assignment as ReportIcon, Refresh as RetryIcon, Refresh, AttachMoney as AttachMoneyIcon
} from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';
import { useApp } from '../context/AppContext';
import { getDefaultKimiPrompt, getPromptConfig, setPromptConfig } from '../api/api';

const API_BASE = process.env.REACT_APP_API_BASE || `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api`;

const CSVUploadManager = () => {
    const { apiActions } = useApp();

    // Mode toggle
    const [viewMode, setViewMode] = useState('recent'); // 'recent' or 'upload'

    // Recent imports state
    const [recentImports, setRecentImports] = useState([]);
    const [loadingRecent, setLoadingRecent] = useState(true);

    // Upload state
    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [uploadResult, setUploadResult] = useState(null);

    // Current import state
    const [currentImport, setCurrentImport] = useState(null);
    const [facilityChanges, setFacilityChanges] = useState([]);
    const [loading, setLoading] = useState(false);
    const [expandedChanges, setExpandedChanges] = useState({});
    const [selectedFacilities, setSelectedFacilities] = useState([]);

    // Prompt state
    const [promptTemplates, setPromptTemplates] = useState([]);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [promptText, setPromptText] = useState('');
    const [availableVariables, setAvailableVariables] = useState({});
    const [savingPrompt, setSavingPrompt] = useState(false);

    // Analysis results state
    const [facilityAnalysisResults, setFacilityAnalysisResults] = useState({});

    // Kiddie error state
    const [kiddieErrorFilter, setKiddieErrorFilter] = useState('all'); // 'all', 'errors_only', 'no_errors'
    const [facilitiesWithErrors, setFacilitiesWithErrors] = useState([]);

    // Dialog state
    const [showProcessDialog, setShowProcessDialog] = useState(false);
    const [forceReprocess, setForceReprocess] = useState(false);
    const [showAnalysisDialog, setShowAnalysisDialog] = useState(false);
    const [selectedAnalysisData, setSelectedAnalysisData] = useState(null);
    const [sendToKiddieLoading, setSendToKiddieLoading] = useState(false);
    const [sendInspectionReportsLoading, setSendInspectionReportsLoading] = useState(false);
    const [retryFailedReportsLoading, setRetryFailedReportsLoading] = useState(false);

    // Field selection modal state
    const [showFieldSelectionModal, setShowFieldSelectionModal] = useState(false);
    const [fieldOptions, setFieldOptions] = useState(null);
    const [fieldSelection, setFieldSelection] = useState(null);
    const [loadingFieldOptions, setLoadingFieldOptions] = useState(false);

    // Google features state
    const [googleImagesLoading, setGoogleImagesLoading] = useState(false);
    const [googleDataLoading, setGoogleDataLoading] = useState(false);
    const [scrapeReportsLoading, setScrapeReportsLoading] = useState(false);
    const [analyzePricingLoading, setAnalyzePricingLoading] = useState(false);

    // Tab state
    const [tabValue, setTabValue] = useState(0);

    // Inspection report exports state
    const [inspectionExports, setInspectionExports] = useState([]);
    const [loadingExports, setLoadingExports] = useState(false);
    const [exportingReports, setExportingReports] = useState(false);

    // Filter and pagination state
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchFilter, setSearchFilter] = useState('');
    const [changeTypeFilter, setChangeTypeFilter] = useState('all'); // 'all', 'new', 'changed', 'unchanged'
    const [facilityTypeFilter, setFacilityTypeFilter] = useState('all'); // 'all', 'KDV', 'BSO', etc.
    const [municipalityFilter, setMunicipalityFilter] = useState('all'); // Filter by municipality
    const [cityFilter, setCityFilter] = useState('all'); // Filter by city
    const [statusFilters, setStatusFilters] = useState({}); // {status: 'include'|'exclude'} e.g. {processed: 'include', google_data_scraped: 'exclude'}
    const [confidenceFilter, setConfidenceFilter] = useState('all'); // 'all', 'high', 'medium', 'low'
    // Inspection reports filters
    const [inspectionDateFrom, setInspectionDateFrom] = useState(''); // YYYY-MM-DD
    const [inspectionDateTo, setInspectionDateTo] = useState('');   // YYYY-MM-DD

    // Reset pagination when filters change
    React.useEffect(() => {
        setPage(0);
    }, [searchFilter, changeTypeFilter, facilityTypeFilter, municipalityFilter, cityFilter, statusFilters, confidenceFilter, kiddieErrorFilter, inspectionDateFrom, inspectionDateTo]);

    // Load analysis results when facility changes are loaded
    React.useEffect(() => {
        if (facilityChanges && facilityChanges.length > 0) {
            loadFacilityAnalysisResults();
            loadFacilitiesWithKiddieErrors();
        }
    }, [facilityChanges]);

    useEffect(() => {
        const initializePrompts = async () => {
            await loadRecentImports();
            await loadPromptTemplates();
            await loadAvailableVariables();
            // Load saved prompt after templates to override default
            await loadCurrentPrompt();
        };
        initializePrompts();
    }, []);

    const loadRecentImports = async () => {
        try {
            setLoadingRecent(true);
            const response = await fetch(`${API_BASE}/csv/imports`);
            if (response.ok) {
                const data = await response.json();
                setRecentImports(data.imports || []);

                // Auto-select the most recent import if any exist
                if (data.imports && data.imports.length > 0) {
                    await selectImport(data.imports[0].id);
                }
            } else {
                throw new Error('Failed to load recent imports');
            }
        } catch (error) {
            console.error('Error loading recent imports:', error);
            apiActions.addNotification('Error loading recent imports', 'error');
        } finally {
            setLoadingRecent(false);
        }
    };

    const selectImport = async (importId) => {
        try {
            setLoading(true);
            const response = await fetch(`${API_BASE}/csv/imports/${importId}`);
            if (response.ok) {
                const data = await response.json();
                setCurrentImport(data.import_details);
                setFacilityChanges(data.facility_changes || []);
                setTabValue(1); // Switch to results tab
                apiActions.addNotification(`Loaded import: ${data.import_details.filename}`, 'success');
            } else {
                throw new Error('Failed to load import details');
            }
        } catch (error) {
            console.error('Error loading import:', error);
            apiActions.addNotification('Error loading import details', 'error');
        } finally {
            setLoading(false);
        }
    };

    const loadPromptTemplates = async () => {
        try {
            const response = await fetch(`${API_BASE}/csv/prompt-templates`);
            const data = await response.json();
            setPromptTemplates(data.templates);

            // Set default prompt
            const defaultTemplate = data.templates.find(t => t.is_default);
            if (defaultTemplate) {
                setPromptText(defaultTemplate.prompt_text);
            }
        } catch (err) {
            console.error('Failed to load prompt templates:', err);
        }
    };

    const loadAvailableVariables = async () => {
        try {
            const response = await fetch(`${API_BASE}/csv/prompt-variables`);
            const data = await response.json();
            setAvailableVariables(data.variables);
        } catch (err) {
            console.error('Error loading variables:', err);
        }
    };

    const loadInspectionExports = async () => {
        setLoadingExports(true);
        try {
            const response = await fetch(`${API_BASE}/csv/export-inspection-reports`);
            const data = await response.json();
            setInspectionExports(data.exports || []);
        } catch (error) {
            console.error('Error loading inspection exports:', error);
            apiActions.addNotification('Failed to load inspection exports', 'error');
        } finally {
            setLoadingExports(false);
        }
    };

    // Load exports when switching to Inspection Reports tab
    React.useEffect(() => {
        if (tabValue === 3) {
            loadInspectionExports();
            // Poll for updates every 5 seconds while on this tab
            const interval = setInterval(loadInspectionExports, 5000);
            return () => clearInterval(interval);
        }
    }, [tabValue]);

    const loadCurrentPrompt = async () => {
        try {
            const data = await getPromptConfig();
            // analysis_prompt will now include the default Kimi prompt if none is saved
            if (data.analysis_prompt && data.analysis_prompt.trim()) {
                setPromptText(data.analysis_prompt);
            }
        } catch (err) {
            console.error('Error loading current prompt:', err);
            // Try to load default prompt as final fallback
            try {
                const defaultData = await getDefaultKimiPrompt();
                setPromptText(defaultData.prompt);
            } catch (defaultErr) {
                console.error('Error loading default prompt:', defaultErr);
            }
        }
    };

    const saveCurrentPrompt = async () => {
        try {
            setSavingPrompt(true);
            const result = await setPromptConfig({ analysis_prompt: promptText });

            if (result.status === "ok") {
                apiActions.addNotification('Prompt saved successfully', 'success');
            } else {
                const error = result.message || 'Failed to save prompt';
                throw new Error(error);
            }
        } catch (err) {
            console.error('Error saving prompt:', err);
            apiActions.addNotification('Failed to save prompt: ' + err.message, 'error');
        } finally {
            setSavingPrompt(false);
        }
    };

    const resetToDefaultPrompt = async () => {
        try {
            const defaultData = await getDefaultKimiPrompt();
            setPromptText(defaultData.prompt);
            apiActions.addNotification('Reset to default Kimi prompt', 'success');
        } catch (err) {
            console.error('Error loading default prompt:', err);
            apiActions.addNotification('Failed to load default prompt: ' + err.message, 'error');
        }
    };

    const loadFacilityAnalysisResults = async () => {
        if (!facilityChanges || facilityChanges.length === 0) return;

        try {
            console.log('📊 SIMPLE: Loading analysis results by LRK ID...');
            console.log('🔍 CSV facility LRK IDs:', facilityChanges.map(c => c.lrk_id));

            // SIMPLE: Get analysis summaries (already keyed by LRK ID from database)
            const data = await apiActions.getAnalysisSummaries();

            if (data.success && data.summaries) {
                console.log(`✅ Loaded ${data.count} analysis summaries`);
                console.log('🔍 Analysis results keys:', Object.keys(data.summaries));

                // SIMPLE: Use the summaries directly (they're already keyed by LRK ID)
                setFacilityAnalysisResults(data.summaries);
            } else {
                console.warn('⚠️ Analysis summaries response not successful:', data);
                setFacilityAnalysisResults({});
            }
        } catch (err) {
            console.error('💥 Error loading facility analysis results:', err);
            setFacilityAnalysisResults({});
        }
    };

    const loadFacilitiesWithKiddieErrors = async () => {
        try {
            console.log('🚨 Loading facilities with Kiddie errors...');
            const response = await fetch(`${API_BASE}/facilities/kiddie-errors`);
            if (response.ok) {
                const data = await response.json();
                if (data.success && data.facilities) {
                    console.log(`✅ Loaded ${data.facilities.length} facilities with Kiddie errors`);
                    setFacilitiesWithErrors(data.facilities);
                } else {
                    console.warn('⚠️ Kiddie errors response not successful:', data);
                    setFacilitiesWithErrors([]);
                }
            } else {
                throw new Error('Failed to load facilities with Kiddie errors');
            }
        } catch (err) {
            console.error('💥 Error loading facilities with Kiddie errors:', err);
            setFacilitiesWithErrors([]);
        }
    };

    const showAnalysisDetails = async (facilityChange, analysisSummary) => {
        try {
            console.log('🔍 Loading details for LRK ID:', facilityChange.lrk_id);
            console.log('📊 Available summary:', analysisSummary);

            // Fetch detailed analysis results using the facility details API
            const facilityDetails = await apiActions.getFacilityDetails(facilityChange.lrk_id);

            console.log('✅ Facility details response:', facilityDetails);
            console.log('📋 Analysis results:', facilityDetails.analysis_results);

            // Check if we have CSV analysis results specifically
            const hasCSVAnalysis = facilityDetails.analysis_results?.csv_kimi_analysis;
            console.log('📄 Has CSV analysis:', hasCSVAnalysis);

            setSelectedAnalysisData({
                facility: facilityChange,
                summary: analysisSummary,
                results: facilityDetails.analysis_results || {}
            });
            setShowAnalysisDialog(true);
        } catch (err) {
            console.error('❌ Error loading detailed analysis results:', err);
            console.log('🔍 LRK ID that failed:', facilityChange.lrk_id);

            // Show detailed error message
            const errorMsg = err.response?.data?.detail || err.message || 'Unknown error';
            apiActions.addNotification(`Failed to load analysis details: ${errorMsg}`, 'error');

            // For debugging, still open the dialog with whatever we have
            setSelectedAnalysisData({
                facility: facilityChange,
                summary: analysisSummary,
                results: {},
                error: errorMsg
            });
            setShowAnalysisDialog(true);
        }
    };

    // Upload functionality
    const onDrop = React.useCallback(async (acceptedFiles) => {
        const uploadedFile = acceptedFiles[0];
        if (!uploadedFile) return;

        if (!uploadedFile.name.toLowerCase().endsWith('.csv')) {
            apiActions.addNotification('Please upload a CSV file', 'error');
            return;
        }

        await uploadCSV(uploadedFile);
    }, []);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: {
            'text/csv': ['.csv'],
            'application/vnd.ms-excel': ['.csv']
        },
        multiple: false
    });

    const uploadCSV = async (file) => {
        setUploading(true);
        setUploadResult(null);

        try {
            const formData = new FormData();
            formData.append('file', file);

            const response = await fetch(`${API_BASE}/csv/upload`, {
                method: 'POST',
                body: formData
            });

            const result = await response.json();

            if (result.success) {
                setUploadResult(result);
                apiActions.addNotification(
                    `Successfully processed ${result.total_facilities} facilities. ${result.new_facilities} new, ${result.changed_facilities} changed.`,
                    'success'
                );

                // Load the new import details
                await selectImport(result.import_id);

                // Refresh the recent imports list
                await loadRecentImports();
            } else {
                setUploadResult(result);
                apiActions.addNotification(result.message || 'Failed to upload CSV', 'error');
            }
        } catch (err) {
            const errorResult = { success: false, message: `Upload failed: ${err.message}` };
            setUploadResult(errorResult);
            apiActions.addNotification(errorResult.message, 'error');
        } finally {
            setUploading(false);
        }
    };

    const startBatchProcessing = async () => {
        if (!currentImport || selectedFacilities.length === 0) return;

        setLoading(true);
        try {
            const response = await fetch(`${API_BASE}/csv/batch-process`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    import_id: currentImport.id,
                    facility_changes: selectedFacilities,
                    prompt_text: promptText,
                    force_reprocess: forceReprocess
                })
            });

            const result = await response.json();

            if (result.success) {
                apiActions.addNotification(result.message, 'success');
                setShowProcessDialog(false);
                setSelectedFacilities([]);
            } else {
                apiActions.addNotification('Failed to start batch processing', 'error');
            }
        } catch (err) {
            apiActions.addNotification(`Failed to start processing: ${err.message}`, 'error');
        } finally {
            setLoading(false);
        }
    };

    // Helper functions
    const getChangeTypeColor = (changeType) => {
        switch (changeType) {
            case 'new': return 'success';
            case 'changed': return 'warning';
            case 'unchanged': return 'default';
            default: return 'default';
        }
    };

    const getChangeTypeIcon = (changeType) => {
        switch (changeType) {
            case 'new': return <CheckCircleIcon fontSize="small" />;
            case 'changed': return <InfoIcon fontSize="small" />;
            case 'unchanged': return <CheckCircleIcon fontSize="small" />;
            default: return null;
        }
    };

    const toggleChangeExpansion = (changeId) => {
        setExpandedChanges(prev => ({
            ...prev,
            [changeId]: !prev[changeId]
        }));
    };

    const handleSelectAll = (event) => {
        if (event.target.checked) {
            // Use filtered facilities instead of all facilities
            const eligibleChanges = filteredFacilities
                .filter(change => change.change_type !== 'unchanged')
                .map(change => change.id);
            setSelectedFacilities(eligibleChanges);
        } else {
            setSelectedFacilities([]);
        }
    };

    const handleSelectFacility = (changeId) => {
        setSelectedFacilities(prev =>
            prev.includes(changeId)
                ? prev.filter(id => id !== changeId)
                : [...prev, changeId]
        );
    };

    // Helper function to get confidence score from analysis results
    const getConfidenceScore = (change) => {
        const analysisResults = facilityAnalysisResults[change.lrk_id];
        if (!analysisResults) return null;

        // Check for CSV Kimi analysis confidence (same logic as the table display)
        const csvConfidence = analysisResults?.csv_kimi_confidence ||
            analysisResults?.analyses?.csv_kimi_analysis?.result?.confidence;

        // Check for regular Kimi analysis confidence
        const kimiConfidence = analysisResults?.kimi_confidence;

        return csvConfidence !== undefined ? csvConfidence :
            kimiConfidence !== undefined ? kimiConfidence : null;
    };

    // Helper function to categorize confidence score
    const getConfidenceCategory = (confidence) => {
        if (confidence === null || confidence === undefined) return 'none';
        if (confidence >= 0.7) return 'high';
        if (confidence >= 0.4) return 'medium';
        return 'low';
    };

    // Filter and pagination logic
    const filteredFacilities = React.useMemo(() => {
        let filtered = facilityChanges.filter(change => {
            // Search filter (name, lrk_id, address)
            if (searchFilter) {
                const searchLower = searchFilter.toLowerCase();
                const name = change.new_data?.actuele_naam_oko || change.new_data?.name || '';
                const address = change.new_data?.opvanglocatie_adres || change.new_data?.address || '';
                const lrkId = change.lrk_id || '';

                if (!name.toLowerCase().includes(searchLower) &&
                    !address.toLowerCase().includes(searchLower) &&
                    !lrkId.toLowerCase().includes(searchLower)) {
                    return false;
                }
            }

            // Change type filter
            if (changeTypeFilter !== 'all' && change.change_type !== changeTypeFilter) {
                return false;
            }

            // Facility type filter
            if (facilityTypeFilter !== 'all') {
                const facilityType = change.new_data?.type_oko || change.new_data?.type || '';
                if (facilityType !== facilityTypeFilter) {
                    return false;
                }
            }

            // Municipality filter
            if (municipalityFilter !== 'all') {
                const municipality = change.new_data?.verantwoordelijke_gemeente || change.new_data?.municipality || '';
                if (municipality !== municipalityFilter) {
                    return false;
                }
            }

            // City filter
            if (cityFilter !== 'all') {
                const city = change.new_data?.opvanglocatie_woonplaats || change.new_data?.city || '';
                if (city !== cityFilter) {
                    return false;
                }
            }

            // Status filters - Per-item include/exclude modes
            if (Object.keys(statusFilters).length > 0) {
                for (const [statusType, mode] of Object.entries(statusFilters)) {
                    let hasStatus = false;

                    switch (statusType) {
                        case 'processed':
                            hasStatus = change.processed || false;
                            break;
                        case 'google_data_scraped':
                            hasStatus = change.google_data_status === 'completed';
                            break;
                        case 'images_saved':
                            hasStatus = change.images_status === 'completed';
                            break;
                        case 'sent_to_kiddie':
                            hasStatus = change.sent_to_kiddie_status === 'sent';
                            break;
                        case 'inspection_reports_scraped':
                            hasStatus = change.inspection_reports_status === 'scraped';
                            break;
                        default:
                            hasStatus = false;
                    }

                    if (mode === 'include' && !hasStatus) {
                        return false; // Include mode: must have this status
                    }
                    if (mode === 'exclude' && hasStatus) {
                        return false; // Exclude mode: must NOT have this status
                    }
                }
            }

            // Confidence score filter
            if (confidenceFilter !== 'all') {
                const confidence = getConfidenceScore(change);
                const category = getConfidenceCategory(confidence);
                if (confidenceFilter !== category) {
                    return false;
                }
            }

            // Kiddie error filter
            if (kiddieErrorFilter !== 'all') {
                const hasKiddieError = facilitiesWithErrors.some(errorFacility => errorFacility.lrk_id === change.lrk_id);
                if (kiddieErrorFilter === 'errors_only' && !hasKiddieError) {
                    return false;
                }
                if (kiddieErrorFilter === 'no_errors' && hasKiddieError) {
                    return false;
                }
            }

            // Inspection reports date filter (using actual inspection date, inclusive range)
            if (inspectionDateFrom || inspectionDateTo) {
                const inspectionDateStr = change.newest_inspection_date;
                if (!inspectionDateStr) {
                    return false;
                }
                const inspectionDate = new Date(inspectionDateStr);
                if (Number.isNaN(inspectionDate.getTime())) {
                    return false;
                }
                if (inspectionDateFrom) {
                    const from = new Date(inspectionDateFrom + 'T00:00:00');
                    if (inspectionDate < from) return false;
                }
                if (inspectionDateTo) {
                    const to = new Date(inspectionDateTo + 'T23:59:59');
                    if (inspectionDate > to) return false;
                }
            }

            return true;
        });

        return filtered;
    }, [facilityChanges, searchFilter, changeTypeFilter, facilityTypeFilter, municipalityFilter, cityFilter, statusFilters, confidenceFilter, kiddieErrorFilter, inspectionDateFrom, inspectionDateTo, facilitiesWithErrors, facilityAnalysisResults]);

    const paginatedFacilities = React.useMemo(() => {
        const start = page * rowsPerPage;
        const end = start + rowsPerPage;
        return filteredFacilities.slice(start, end);
    }, [filteredFacilities, page, rowsPerPage]);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const clearFilters = () => {
        setSearchFilter('');
        setChangeTypeFilter('all');
        setFacilityTypeFilter('all');
        setMunicipalityFilter('all');
        setCityFilter('all');
        setStatusFilters({});
        setConfidenceFilter('all');
        setKiddieErrorFilter('all');
        setInspectionDateFrom('');
        setInspectionDateTo('');
        setPage(0);
    };

    // Get unique facility types for filter dropdown
    const uniqueFacilityTypes = React.useMemo(() => {
        const types = new Set();
        facilityChanges.forEach(change => {
            const type = change.new_data?.type_oko || change.new_data?.type;
            if (type) types.add(type);
        });
        return Array.from(types).sort();
    }, [facilityChanges]);

    // Get unique municipalities for filter dropdown
    const uniqueMunicipalities = React.useMemo(() => {
        const municipalities = new Set();
        facilityChanges.forEach(change => {
            const municipality = change.new_data?.verantwoordelijke_gemeente || change.new_data?.municipality;
            if (municipality) municipalities.add(municipality);
        });
        return Array.from(municipalities).sort();
    }, [facilityChanges]);

    // Get unique cities for filter dropdown
    const uniqueCities = React.useMemo(() => {
        const cities = new Set();
        facilityChanges.forEach(change => {
            const city = change.new_data?.opvanglocatie_woonplaats || change.new_data?.city;
            if (city) cities.add(city);
        });
        return Array.from(cities).sort();
    }, [facilityChanges]);

    // Helper function to count processed selected facilities
    const getProcessedSelectedCount = () => {
        return facilityChanges.filter(change =>
            selectedFacilities.includes(change.id) &&
            change.processed &&
            change.lrk_id
        ).length;
    };

    const sendToKiddie = async () => {
        if (!currentImport || selectedFacilities.length === 0) return;

        // Filter to only include processed facilities with analysis data
        const processedFacilities = facilityChanges
            .filter(change =>
                selectedFacilities.includes(change.id) &&
                change.processed &&
                change.lrk_id
            )
            .map(change => change.lrk_id);

        if (processedFacilities.length === 0) {
            apiActions.addNotification('No processed facilities with analysis data selected', 'warning');
            return;
        }

        setSendToKiddieLoading(true);
        try {
            const payload = {
                facility_ids: processedFacilities,
                include_pricing: true,
                max_concurrent: 2
            };

            const response = await fetch(`${API_BASE}/facilities/send-to-kiddie`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });

            const result = await response.json();

            if (result.success) {
                apiActions.addNotification(
                    `Successfully queued ${processedFacilities.length} facilities for Kiddie! Task ID: ${result.task_id}`,
                    'success'
                );
                setSelectedFacilities([]);
            } else {
                apiActions.addNotification('Failed to queue Kiddie send tasks', 'error');
            }
        } catch (err) {
            apiActions.addNotification(`Failed to start Kiddie send: ${err.message}`, 'error');
        } finally {
            setSendToKiddieLoading(false);
        }
    };

    const sendToKiddieWithSelection = async (customFieldSelection = null) => {
        if (!currentImport || selectedFacilities.length === 0) return;

        // Filter to only include processed facilities with analysis data
        const processedFacilities = facilityChanges
            .filter(change =>
                selectedFacilities.includes(change.id) &&
                change.processed &&
                change.lrk_id
            )
            .map(change => change.lrk_id);

        if (processedFacilities.length === 0) {
            apiActions.addNotification('No processed facilities with analysis data selected', 'warning');
            return;
        }

        setSendToKiddieLoading(true);
        try {
            const payload = {
                facility_ids: processedFacilities,
                include_pricing: true,
                max_concurrent: 2
            };

            // Add field selection if provided
            if (customFieldSelection) {
                payload.field_selection = customFieldSelection;
            }

            const response = await fetch(`${API_BASE}/facilities/send-to-kiddie`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });

            const result = await response.json();

            if (result.success) {
                const selectionText = result.field_selection ? " with custom field selection" : "";
                apiActions.addNotification(
                    `Successfully queued ${processedFacilities.length} facilities for Kiddie${selectionText}! Task ID: ${result.task_id}`,
                    'success'
                );
                setSelectedFacilities([]);
                setShowFieldSelectionModal(false);
            } else {
                apiActions.addNotification('Failed to queue Kiddie send tasks', 'error');
            }
        } catch (err) {
            apiActions.addNotification(`Failed to send to Kiddie: ${err.message}`, 'error');
        } finally {
            setSendToKiddieLoading(false);
        }
    };

    const sendInspectionReportsToKiddie = async () => {
        if (!currentImport || selectedFacilities.length === 0) return;

        // Filter to only include processed facilities with analysis data
        const processedFacilities = facilityChanges
            .filter(change =>
                selectedFacilities.includes(change.id) &&
                change.processed &&
                change.lrk_id
            )
            .map(change => change.lrk_id);

        if (processedFacilities.length === 0) {
            apiActions.addNotification('No processed facilities with analysis data selected', 'warning');
            return;
        }

        setSendInspectionReportsLoading(true);
        try {
            const payload = {
                facility_ids: processedFacilities,
                max_concurrent: 2
            };

            const response = await fetch(`${API_BASE}/facilities/send-inspection-reports-to-kiddie`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });

            const result = await response.json();

            if (result.success) {
                apiActions.addNotification(
                    `Successfully queued ${processedFacilities.length} inspection reports for Kiddie! Task ID: ${result.task_id}`,
                    'success'
                );
                setSelectedFacilities([]);
            } else {
                apiActions.addNotification('Failed to queue inspection reports send tasks', 'error');
            }
        } catch (err) {
            apiActions.addNotification(`Failed to start inspection reports send: ${err.message}`, 'error');
        } finally {
            setSendInspectionReportsLoading(false);
        }
    };

    const retryFailedInspectionReports = async () => {
        setRetryFailedReportsLoading(true);
        try {
            // First get the list of failed reports to show user what we're retrying
            const failedResponse = await fetch(`${API_BASE}/inspection-reports/failed`);
            const failedData = await failedResponse.json();

            if (!failedData.success || failedData.total_failed === 0) {
                apiActions.addNotification('No failed inspection reports found to retry', 'info');
                return;
            }

            // Start the retry process for all failed reports
            const retryResponse = await fetch(`${API_BASE}/inspection-reports/retry-failed`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    report_ids: null, // null means retry all failed reports
                    max_concurrent: 1
                })
            });

            const retryResult = await retryResponse.json();

            if (retryResult.success) {
                apiActions.addNotification(
                    `Successfully queued retry for ${failedData.total_failed} failed inspection reports! Task ID: ${retryResult.task_id}`,
                    'success'
                );
            } else {
                apiActions.addNotification('Failed to queue retry tasks for failed inspection reports', 'error');
            }
        } catch (err) {
            apiActions.addNotification(`Failed to start retry process: ${err.message}`, 'error');
        } finally {
            setRetryFailedReportsLoading(false);
        }
    };

    const handleGoogleImagesSearch = async () => {
        if (!currentImport || selectedFacilities.length === 0) return;

        // Filter to only include processed facilities with LRK IDs
        const processedFacilities = facilityChanges
            .filter(change =>
                selectedFacilities.includes(change.id) &&
                change.processed &&
                change.lrk_id
            )
            .map(change => change.lrk_id);

        if (processedFacilities.length === 0) {
            apiActions.addNotification('No processed facilities with LRK IDs selected', 'warning');
            return;
        }

        setGoogleImagesLoading(true);
        try {
            const response = await fetch(`${API_BASE}/facilities/find-google-images`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    facility_ids: processedFacilities
                })
            });

            const result = await response.json();

            if (result.success) {
                apiActions.addNotification(
                    `Successfully queued Google Images search for ${processedFacilities.length} facilities! Task ID: ${result.task_id}`,
                    'success'
                );
                setSelectedFacilities([]);
            } else {
                apiActions.addNotification('Failed to queue Google Images search tasks', 'error');
            }
        } catch (err) {
            apiActions.addNotification(`Failed to start Google Images search: ${err.message}`, 'error');
        } finally {
            setGoogleImagesLoading(false);
        }
    };

    const handleGoogleDataSearch = async () => {
        if (!currentImport || selectedFacilities.length === 0) return;

        // Filter to only include processed facilities with LRK IDs
        const processedFacilities = facilityChanges
            .filter(change =>
                selectedFacilities.includes(change.id) &&
                change.processed &&
                change.lrk_id
            )
            .map(change => change.lrk_id);

        if (processedFacilities.length === 0) {
            apiActions.addNotification('No processed facilities with LRK IDs selected', 'warning');
            return;
        }

        setGoogleDataLoading(true);
        try {
            const response = await fetch(`${API_BASE}/facilities/find-google-data`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    facility_ids: processedFacilities
                })
            });

            const result = await response.json();

            if (result.success) {
                apiActions.addNotification(
                    `Successfully queued Google Data search for ${processedFacilities.length} facilities! Task ID: ${result.task_id}`,
                    'success'
                );
                setSelectedFacilities([]);
            } else {
                apiActions.addNotification('Failed to queue Google Data search tasks', 'error');
            }
        } catch (err) {
            apiActions.addNotification(`Failed to start Google Data search: ${err.message}`, 'error');
        } finally {
            setGoogleDataLoading(false);
        }
    };

    const handleScrapeReports = async () => {
        if (!currentImport || selectedFacilities.length === 0) return;

        const processedFacilities = facilityChanges
            .filter(change =>
                selectedFacilities.includes(change.id) &&
                change.processed &&
                change.lrk_id
            )
            .map(change => change.lrk_id);

        if (processedFacilities.length === 0) {
            apiActions.addNotification('No processed facilities with LRK IDs selected', 'warning');
            return;
        }

        setScrapeReportsLoading(true);
        try {
            const response = await fetch(`${API_BASE}/facilities/scrape-reports`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ facility_ids: processedFacilities })
            });
            const result = await response.json();
            if (result.success) {
                apiActions.addNotification(
                    `Queued LRK reports scraping for ${processedFacilities.length} facilities. Task ID: ${result.task_id}`,
                    'success'
                );
                setSelectedFacilities([]);
            } else {
                apiActions.addNotification('Failed to queue LRK reports scraping', 'error');
            }
        } catch (err) {
            apiActions.addNotification(`Failed to start LRK reports scraping: ${err.message}`, 'error');
        } finally {
            setScrapeReportsLoading(false);
        }
    };

    const handleAnalyzePricing = async () => {
        if (!currentImport || selectedFacilities.length === 0) return;

        // Filter to only include processed facilities with LRK IDs
        const processedFacilities = facilityChanges
            .filter(change =>
                selectedFacilities.includes(change.id) &&
                change.processed &&
                change.lrk_id
            )
            .map(change => change.lrk_id);

        if (processedFacilities.length === 0) {
            apiActions.addNotification('No processed facilities selected', 'warning');
            return;
        }

        setAnalyzePricingLoading(true);
        try {
            const response = await fetch(`${API_BASE}/api/csv/analyze-pricing-bulk`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    facility_ids: processedFacilities,
                    force_reanalysis: true
                })
            });

            const result = await response.json();

            if (result.success) {
                apiActions.addNotification(
                    `Successfully queued pricing analysis for ${processedFacilities.length} facilities! ${result.task_count} tasks created.`,
                    'success'
                );
                setSelectedFacilities([]);
            } else {
                apiActions.addNotification('Failed to queue pricing analysis tasks', 'error');
            }
        } catch (err) {
            apiActions.addNotification(`Failed to start pricing analysis: ${err.message}`, 'error');
        } finally {
            setAnalyzePricingLoading(false);
        }
    };

    const handleExportInspectionReports = async () => {
        if (!currentImport || selectedFacilities.length === 0) return;

        // Get all selected facilities' LRK IDs (doesn't need to be processed)
        const selectedLrkIds = facilityChanges
            .filter(change => selectedFacilities.includes(change.id) && change.lrk_id)
            .map(change => change.lrk_id);

        if (selectedLrkIds.length === 0) {
            apiActions.addNotification('No facilities with LRK IDs selected', 'warning');
            return;
        }

        setExportingReports(true);
        try {
            const response = await fetch(`${API_BASE}/csv/export-inspection-reports`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    lrk_ids: selectedLrkIds,
                    csv_import_id: currentImport.id
                })
            });
            const result = await response.json();
            
            if (result.success) {
                apiActions.addNotification(
                    `Export job started for ${selectedLrkIds.length} facilities! Check the "Inspection Reports" tab for progress.`,
                    'success'
                );
                setSelectedFacilities([]);
                // Switch to Inspection Reports tab to show progress
                setTabValue(3);
                // Reload exports list
                await loadInspectionExports();
            } else {
                apiActions.addNotification('Failed to start inspection reports export', 'error');
            }
        } catch (err) {
            apiActions.addNotification(`Failed to export inspection reports: ${err.message}`, 'error');
        } finally {
            setExportingReports(false);
        }
    };

    // Render components
    const renderUploadArea = () => (
        <Card sx={{ mb: 3 }}>
            <CardContent>
                <Typography variant="h6" gutterBottom>
                    Upload CSV File
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                    Upload a CSV file from the LRK to compare with existing facility data
                </Typography>

                <Box
                    {...getRootProps()}
                    sx={{
                        border: '2px dashed',
                        borderColor: isDragActive ? 'primary.main' : 'grey.300',
                        borderRadius: 2,
                        p: 3,
                        textAlign: 'center',
                        cursor: 'pointer',
                        bgcolor: isDragActive ? 'action.hover' : 'background.paper',
                        '&:hover': {
                            borderColor: 'primary.main',
                            bgcolor: 'action.hover'
                        }
                    }}
                >
                    <input {...getInputProps()} />
                    <CloudUploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" gutterBottom>
                        {isDragActive ? 'Drop the CSV file here' : 'Drag & drop a CSV file here'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        or click to select a file
                    </Typography>
                    <Button variant="outlined" sx={{ mt: 2 }} disabled={uploading}>
                        Choose File
                    </Button>
                </Box>

                {uploading && (
                    <Box sx={{ mt: 2 }}>
                        <CircularProgress />
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Uploading and analyzing CSV file...
                        </Typography>
                    </Box>
                )}

                {uploadResult && (
                    <Alert severity={uploadResult.success ? 'success' : 'error'} sx={{ mt: 2 }}>
                        {uploadResult.message}
                    </Alert>
                )}
            </CardContent>
        </Card>
    );

    const renderRecentImports = () => (
        <Card sx={{ mb: 3 }}>
            <CardContent>
                <Typography variant="h6" gutterBottom>
                    Recent CSV Imports
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                    Select a previously uploaded CSV file to view its facility changes.
                </Typography>

                {loadingRecent ? (
                    <Box sx={{ mt: 2 }}>
                        <CircularProgress />
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Loading recent imports...
                        </Typography>
                    </Box>
                ) : recentImports.length === 0 ? (
                    <Alert severity="info">
                        No recent CSV imports found. Upload a new file to get started.
                    </Alert>
                ) : (
                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Filename</TableCell>
                                    <TableCell>Date</TableCell>
                                    <TableCell>Facilities</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {recentImports.map((importItem) => (
                                    <TableRow key={importItem.id}>
                                        <TableCell>{importItem.filename}</TableCell>
                                        <TableCell>
                                            {new Date(importItem.uploaded_at).toLocaleDateString()}
                                        </TableCell>
                                        <TableCell>
                                            <Stack direction="row" spacing={1}>
                                                <Chip
                                                    label={`${importItem.total_facilities} total`}
                                                    color="info"
                                                    size="small"
                                                />
                                                {importItem.new_facilities > 0 && (
                                                    <Chip
                                                        label={`${importItem.new_facilities} new`}
                                                        color="success"
                                                        size="small"
                                                    />
                                                )}
                                                {importItem.changed_facilities > 0 && (
                                                    <Chip
                                                        label={`${importItem.changed_facilities} changed`}
                                                        color="warning"
                                                        size="small"
                                                    />
                                                )}
                                            </Stack>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={importItem.status}
                                                color={
                                                    importItem.status === 'completed' ? 'success' :
                                                        importItem.status === 'processing' ? 'warning' : 'default'
                                                }
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Button
                                                variant={currentImport?.id === importItem.id ? "contained" : "outlined"}
                                                size="small"
                                                onClick={() => selectImport(importItem.id)}
                                                disabled={loading}
                                            >
                                                {currentImport?.id === importItem.id ? 'Selected' : 'View Changes'}
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </CardContent>
        </Card>
    );

    const renderFilterBar = () => (
        <Card sx={{ mb: 2 }}>
            <CardContent>
                <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                    <FilterListIcon color="primary" />
                    <Typography variant="h6">Filters</Typography>

                    <TextField
                        label="Search"
                        placeholder="Name, LRK ID, or Address"
                        value={searchFilter}
                        onChange={(e) => setSearchFilter(e.target.value)}
                        size="small"
                        sx={{ minWidth: 200 }}
                    />

                    <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel>Change Type</InputLabel>
                        <Select
                            value={changeTypeFilter}
                            onChange={(e) => setChangeTypeFilter(e.target.value)}
                            label="Change Type"
                        >
                            <MenuItem value="all">All</MenuItem>
                            <MenuItem value="new">New</MenuItem>
                            <MenuItem value="changed">Changed</MenuItem>
                            <MenuItem value="unchanged">Unchanged</MenuItem>
                        </Select>
                    </FormControl>

                    <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel>Facility Type</InputLabel>
                        <Select
                            value={facilityTypeFilter}
                            onChange={(e) => setFacilityTypeFilter(e.target.value)}
                            label="Facility Type"
                        >
                            <MenuItem value="all">All</MenuItem>
                            {uniqueFacilityTypes.map(type => (
                                <MenuItem key={type} value={type}>{type}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>Municipality</InputLabel>
                        <Select
                            value={municipalityFilter}
                            onChange={(e) => setMunicipalityFilter(e.target.value)}
                            label="Municipality"
                        >
                            <MenuItem value="all">All</MenuItem>
                            {uniqueMunicipalities.map(municipality => (
                                <MenuItem key={municipality} value={municipality}>{municipality}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>City</InputLabel>
                        <Select
                            value={cityFilter}
                            onChange={(e) => setCityFilter(e.target.value)}
                            label="City"
                        >
                            <MenuItem value="all">All</MenuItem>
                            {uniqueCities.map(city => (
                                <MenuItem key={city} value={city}>{city}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 300 }}>
                        <Typography variant="body2" color="text.secondary">Status Filters</Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {[
                                { key: 'processed', label: 'Processed' },
                                { key: 'google_data_scraped', label: 'Google Data' },
                                { key: 'images_saved', label: 'Images Saved' },
                                { key: 'sent_to_kiddie', label: 'Sent to Kiddie' },
                                { key: 'inspection_reports_scraped', label: 'Inspection Reports' }
                            ].map(({ key, label }) => {
                                const isActive = statusFilters[key];
                                const mode = statusFilters[key];

                                return (
                                    <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <Chip
                                            label={label}
                                            size="small"
                                            variant={!isActive ? 'outlined' : 'filled'}
                                            color={
                                                !isActive ? 'default' :
                                                    mode === 'include' ? 'primary' : 'error'
                                            }
                                            onClick={() => {
                                                const newFilters = { ...statusFilters };
                                                if (!isActive) {
                                                    // First click: set to include
                                                    newFilters[key] = 'include';
                                                } else if (mode === 'include') {
                                                    // Second click: change to exclude
                                                    newFilters[key] = 'exclude';
                                                } else {
                                                    // Third click: remove filter
                                                    delete newFilters[key];
                                                }
                                                setStatusFilters(newFilters);
                                            }}
                                            icon={
                                                !isActive ? null :
                                                    mode === 'include' ?
                                                        <AddIcon2 sx={{ fontSize: 14 }} /> :
                                                        <RemoveIcon sx={{ fontSize: 14 }} />
                                            }
                                        />
                                    </Box>
                                );
                            })}
                        </Box>
                    </Box>

                    <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel>Confidence</InputLabel>
                        <Select
                            value={confidenceFilter}
                            onChange={(e) => setConfidenceFilter(e.target.value)}
                            label="Confidence"
                        >
                            <MenuItem value="all">All</MenuItem>
                            <MenuItem value="high">High (≥70%)</MenuItem>
                            <MenuItem value="medium">Medium (40-69%)</MenuItem>
                            <MenuItem value="low">Low (&lt;40%)</MenuItem>
                            <MenuItem value="none">No Score</MenuItem>
                        </Select>
                    </FormControl>

                    <FormControl size="small" sx={{ minWidth: 140 }}>
                        <InputLabel>Kiddie Errors</InputLabel>
                        <Select
                            value={kiddieErrorFilter}
                            onChange={(e) => setKiddieErrorFilter(e.target.value)}
                            label="Kiddie Errors"
                        >
                            <MenuItem value="all">All</MenuItem>
                            <MenuItem value="errors_only">Errors Only ({facilitiesWithErrors.length})</MenuItem>
                            <MenuItem value="no_errors">No Errors</MenuItem>
                        </Select>
                    </FormControl>

                    <TextField
                        label="Inspection date from"
                        type="date"
                        size="small"
                        value={inspectionDateFrom}
                        onChange={(e) => setInspectionDateFrom(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                        label="to"
                        type="date"
                        size="small"
                        value={inspectionDateTo}
                        onChange={(e) => setInspectionDateTo(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                    />

                    <Button
                        variant="outlined"
                        onClick={clearFilters}
                        size="small"
                    >
                        Clear Filters
                    </Button>

                    <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
                        Showing {filteredFacilities.length} of {facilityChanges.length} facilities
                    </Typography>
                </Stack>
            </CardContent>
        </Card>
    );

    const renderFacilityChanges = () => {
        if (!facilityChanges.length) {
            return (
                <Alert severity="info">
                    No facility changes to display. Upload a CSV file to get started.
                </Alert>
            );
        }

        const newChanges = facilityChanges.filter(c => c.change_type === 'new');
        const changedChanges = facilityChanges.filter(c => c.change_type === 'changed');
        const unchangedChanges = facilityChanges.filter(c => c.change_type === 'unchanged');

        return (
            <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">
                        Facility Changes ({facilityChanges.length} total, {filteredFacilities.length} filtered)
                    </Typography>
                    <Stack direction="row" spacing={1}>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={() => {
                                // Select all visible (filtered and paginated) facilities that can be selected
                                const selectableIds = paginatedFacilities
                                    .filter(change => change.change_type !== 'unchanged')
                                    .map(change => change.id);
                                setSelectedFacilities(prev => {
                                    const newSelection = new Set([...prev, ...selectableIds]);
                                    return Array.from(newSelection);
                                });
                            }}
                        >
                            Select Page
                        </Button>

                        <Button
                            variant="outlined"
                            size="small"
                            onClick={() => {
                                // Select all filtered facilities that can be selected
                                const selectableIds = filteredFacilities
                                    .filter(change => change.change_type !== 'unchanged')
                                    .map(change => change.id);
                                setSelectedFacilities(selectableIds);
                            }}
                        >
                            Select All Filtered
                        </Button>

                        <Button
                            variant="outlined"
                            size="small"
                            onClick={() => setSelectedFacilities([])}
                        >
                            Clear Selection
                        </Button>

                        <Button
                            variant="contained"
                            startIcon={<PlayArrowIcon />}
                            onClick={() => setShowProcessDialog(true)}
                            disabled={selectedFacilities.length === 0 || loading}
                        >
                            Process Selected ({selectedFacilities.length})
                        </Button>

                        <Button
                            variant="contained"
                            color="success"
                            startIcon={<UploadIcon />}
                            onClick={sendToKiddie}
                            disabled={selectedFacilities.length === 0 || sendToKiddieLoading || getProcessedSelectedCount() === 0}
                        >
                            {sendToKiddieLoading ? <CircularProgress size={20} color="inherit" /> : `Send to Kiddie (${getProcessedSelectedCount()})`}
                        </Button>

                        <Button
                            variant="contained"
                            color="info"
                            startIcon={<ReportIcon />}
                            onClick={sendInspectionReportsToKiddie}
                            disabled={selectedFacilities.length === 0 || sendInspectionReportsLoading || getProcessedSelectedCount() === 0}
                        >
                            {sendInspectionReportsLoading ? <CircularProgress size={20} color="inherit" /> : `Send Reports to Kiddie (${getProcessedSelectedCount()})`}
                        </Button>

                        <Button
                            variant="outlined"
                            color="warning"
                            startIcon={<RetryIcon />}
                            onClick={retryFailedInspectionReports}
                            disabled={retryFailedReportsLoading}
                        >
                            {retryFailedReportsLoading ? <CircularProgress size={20} color="inherit" /> : 'Retry Failed Reports'}
                        </Button>

                        <Button
                            variant="outlined"
                            startIcon={<ImageIcon />}
                            onClick={handleGoogleImagesSearch}
                            disabled={selectedFacilities.length === 0 || googleImagesLoading || getProcessedSelectedCount() === 0}
                            color="secondary"
                        >
                            {googleImagesLoading ? <CircularProgress size={20} color="inherit" /> : `Find Google Images (${getProcessedSelectedCount()})`}
                        </Button>

                        <Button
                            variant="outlined"
                            startIcon={<MapIcon />}
                            onClick={handleGoogleDataSearch}
                            disabled={selectedFacilities.length === 0 || googleDataLoading || getProcessedSelectedCount() === 0}
                            color="primary"
                        >
                            {googleDataLoading ? <CircularProgress size={20} color="inherit" /> : `Find Google Data (${getProcessedSelectedCount()})`}
                        </Button>

                        <Button
                            variant="outlined"
                            startIcon={<DescriptionIcon />}
                            onClick={handleScrapeReports}
                            disabled={selectedFacilities.length === 0 || scrapeReportsLoading || getProcessedSelectedCount() === 0}
                            color="primary"
                        >
                            {scrapeReportsLoading ? <CircularProgress size={20} color="inherit" /> : `Scrape Reports (${getProcessedSelectedCount()})`}
                        </Button>

                        <Button
                            variant="outlined"
                            startIcon={<AttachMoneyIcon />}
                            onClick={handleAnalyzePricing}
                            disabled={selectedFacilities.length === 0 || analyzePricingLoading || getProcessedSelectedCount() === 0}
                            color="secondary"
                        >
                            {analyzePricingLoading ? <CircularProgress size={20} color="inherit" /> : `Analyze Pricing (${getProcessedSelectedCount()})`}
                        </Button>

                        <Button
                            variant="contained"
                            color="secondary"
                            startIcon={<CloudUploadIcon />}
                            onClick={handleExportInspectionReports}
                            disabled={selectedFacilities.length === 0 || exportingReports}
                        >
                            {exportingReports ? <CircularProgress size={20} color="inherit" /> : `Download All Inspection Reports (${selectedFacilities.length})`}
                        </Button>
                    </Stack>
                </Box>

                <Box sx={{ mb: 2 }}>
                    <Stack direction="row" spacing={2}>
                        <Chip
                            label={`${newChanges.length} New`}
                            color="success"
                            size="small"
                        />
                        <Chip
                            label={`${changedChanges.length} Changed`}
                            color="warning"
                            size="small"
                        />
                        <Chip
                            label={`${unchangedChanges.length} Unchanged`}
                            size="small"
                        />
                    </Stack>
                </Box>

                <FormControlLabel
                    control={
                        <Checkbox
                            checked={selectedFacilities.length > 0 &&
                                selectedFacilities.length === filteredFacilities.filter(c => c.change_type !== 'unchanged').length}
                            indeterminate={
                                selectedFacilities.length > 0 &&
                                selectedFacilities.length < filteredFacilities.filter(c => c.change_type !== 'unchanged').length
                            }
                            onChange={handleSelectAll}
                        />
                    }
                    label="Select all filtered (new and changed only)"
                />

                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell padding="checkbox"></TableCell>
                                <TableCell>LRK ID</TableCell>
                                <TableCell>Facility Info</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Review Quality</TableCell>
                                <TableCell>Changed Fields</TableCell>
                                <TableCell>Inspection Reports</TableCell>
                                <TableCell>CSV Data</TableCell>
                                <TableCell>Analysis Results</TableCell>
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {paginatedFacilities.map((change) => (
                                <React.Fragment key={change.id}>
                                    <TableRow>
                                        <TableCell padding="checkbox">
                                            {change.change_type !== 'unchanged' && (
                                                <Checkbox
                                                    checked={selectedFacilities.includes(change.id)}
                                                    onChange={() => handleSelectFacility(change.id)}
                                                />
                                            )}
                                        </TableCell>
                                        <TableCell>{change.lrk_id}</TableCell>
                                        <TableCell>
                                            <Stack direction="column" spacing={0.5}>
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                        {change.new_data?.actuele_naam_oko || change.new_data?.name || 'N/A'}
                                                    </Typography>
                                                    {facilitiesWithErrors.some(errorFacility => errorFacility.lrk_id === change.lrk_id) && (
                                                        <Tooltip title={
                                                            (() => {
                                                                const errorFacility = facilitiesWithErrors.find(f => f.lrk_id === change.lrk_id);
                                                                return errorFacility ? `Kiddie Error: ${errorFacility.kiddie_error_code} - ${errorFacility.kiddie_error_message}` : 'Kiddie API Error';
                                                            })()
                                                        }>
                                                            <ErrorIcon color="error" sx={{ fontSize: 16 }} />
                                                        </Tooltip>
                                                    )}
                                                </Stack>
                                                <Typography variant="body2" color="text.secondary">
                                                    {change.new_data?.type_oko || change.new_data?.type || 'N/A'}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    {change.new_data?.opvanglocatie_adres || change.new_data?.address || 'N/A'}
                                                </Typography>
                                            </Stack>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={change.change_type}
                                                color={getChangeTypeColor(change.change_type)}
                                                icon={getChangeTypeIcon(change.change_type)}
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            {(() => {
                                                const expectedReviews = change.expected_reviews || 0;
                                                const actualReviews = change.actual_reviews || 0;
                                                const reviewQuality = change.review_quality || 'unknown';

                                                const getReviewQualityColor = (quality) => {
                                                    switch (quality) {
                                                        case 'complete': return 'success';
                                                        case 'partial': return 'warning';
                                                        case 'missing': return 'error';
                                                        case 'no_reviews': return 'default';
                                                        default: return 'default';
                                                    }
                                                };

                                                const getReviewQualityLabel = (quality, expected, actual) => {
                                                    switch (quality) {
                                                        case 'complete': return `✓ ${actual}/${expected}`;
                                                        case 'partial': return `⚠ ${actual}/${expected}`;
                                                        case 'missing': return `✗ 0/${expected}`;
                                                        case 'no_reviews': return 'No reviews';
                                                        default: return 'Unknown';
                                                    }
                                                };

                                                return (
                                                    <Chip
                                                        label={getReviewQualityLabel(reviewQuality, expectedReviews, actualReviews)}
                                                        color={getReviewQualityColor(reviewQuality)}
                                                        size="small"
                                                        variant={reviewQuality === 'no_reviews' ? 'outlined' : 'filled'}
                                                    />
                                                );
                                            })()}
                                        </TableCell>
                                        <TableCell>
                                            {change.changed_fields?.length > 0 ? (
                                                <Typography variant="body2">
                                                    {change.changed_fields.join(', ')}
                                                </Typography>
                                            ) : (
                                                <Typography variant="body2" color="text.secondary">
                                                    -
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {(() => {
                                                const count = change.inspection_reports_count || 0;
                                                const status = change.inspection_reports_status || 'none';
                                                const color = count > 0 ? 'success' : (status === 'scraped' ? 'default' : 'default');
                                                const label = count > 0 ? `${count}` : '0';
                                                return (
                                                    <Chip
                                                        label={label}
                                                        color={color}
                                                        size="small"
                                                        variant={count > 0 ? 'filled' : 'outlined'}
                                                    />
                                                );
                                            })()}
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={`${Object.keys(change.new_data || {}).length} fields`}
                                                color="info"
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            {(() => {
                                                // Look up analysis results by LRK ID
                                                const analysisResults = facilityAnalysisResults[change.lrk_id];
                                                const kimiConfidence = analysisResults?.kimi_confidence;
                                                const hasKimiAnalysis = analysisResults?.analyses?.kimi_analysis?.available;
                                                const hasCSVKimiAnalysis = analysisResults?.analyses?.csv_kimi_analysis?.available;

                                                // Check for either regular Kimi analysis or CSV Kimi analysis
                                                if (hasKimiAnalysis && kimiConfidence !== undefined) {
                                                    const confidence = kimiConfidence;
                                                    return (
                                                        <Stack direction="column" spacing={0.5}>
                                                            <Chip
                                                                label="🤖 Analyzed"
                                                                color="success"
                                                                size="small"
                                                                onClick={() => showAnalysisDetails(change, analysisResults)}
                                                                clickable
                                                            />
                                                            <Typography variant="caption" color={confidence > 0.7 ? "success.main" : confidence > 0.4 ? "warning.main" : "error.main"}>
                                                                {(confidence * 100).toFixed(1)}% confidence
                                                            </Typography>
                                                        </Stack>
                                                    );
                                                } else if (hasCSVKimiAnalysis) {
                                                    // CSV analysis available - get confidence from summary or result
                                                    const csvConfidence = analysisResults?.csv_kimi_confidence ||
                                                        analysisResults?.analyses?.csv_kimi_analysis?.result?.confidence;

                                                    return (
                                                        <Stack direction="column" spacing={0.5}>
                                                            <Chip
                                                                label="📄 CSV Analyzed"
                                                                color="info"
                                                                size="small"
                                                                onClick={() => showAnalysisDetails(change, analysisResults)}
                                                                clickable
                                                            />
                                                            {csvConfidence !== undefined && (
                                                                <Typography variant="caption" color={csvConfidence > 0.7 ? "success.main" : csvConfidence > 0.4 ? "warning.main" : "error.main"}>
                                                                    {(csvConfidence * 100).toFixed(1)}% confidence
                                                                </Typography>
                                                            )}
                                                        </Stack>
                                                    );
                                                } else {
                                                    return (
                                                        <Chip
                                                            label="Not analyzed"
                                                            color="default"
                                                            size="small"
                                                        />
                                                    );
                                                }
                                            })()}
                                        </TableCell>
                                        <TableCell>
                                            <IconButton
                                                size="small"
                                                onClick={() => toggleChangeExpansion(change.id)}
                                            >
                                                {expandedChanges[change.id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                    {expandedChanges[change.id] && (
                                        <TableRow>
                                            <TableCell colSpan={8}>
                                                <Collapse in={true}>
                                                    <Box sx={{ margin: 1 }}>
                                                        <Grid container spacing={2}>
                                                            <Grid item xs={12}>
                                                                <Typography variant="subtitle2" gutterBottom>
                                                                    CSV Data (Available for Kimi Prompts)
                                                                </Typography>
                                                                <Paper sx={{ p: 1, bgcolor: 'grey.50', maxHeight: 300, overflow: 'auto' }}>
                                                                    <Box sx={{ fontSize: '0.75rem' }}>
                                                                        {/* Show key LRK fields in a readable format */}
                                                                        <Box sx={{ mb: 1 }}>
                                                                            <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                                                                                Basic Info:
                                                                            </Typography>
                                                                            <Box sx={{ ml: 1 }}>
                                                                                <div>Name: {change.new_data?.actuele_naam_oko || change.new_data?.name || 'N/A'}</div>
                                                                                <div>Type: {change.new_data?.type_oko || change.new_data?.type || 'N/A'}</div>
                                                                                <div>Capacity: {change.new_data?.aantal_kindplaatsen || change.new_data?.capacity || 'N/A'}</div>
                                                                                <div>Status: {change.new_data?.status || 'N/A'}</div>
                                                                            </Box>
                                                                        </Box>

                                                                        <Box sx={{ mb: 1 }}>
                                                                            <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                                                                                Location:
                                                                            </Typography>
                                                                            <Box sx={{ ml: 1 }}>
                                                                                <div>Address: {change.new_data?.opvanglocatie_adres || change.new_data?.address || 'N/A'}</div>
                                                                                <div>Postal Code: {change.new_data?.opvanglocatie_postcode || change.new_data?.postal_code || 'N/A'}</div>
                                                                                <div>City: {change.new_data?.opvanglocatie_woonplaats || change.new_data?.city || 'N/A'}</div>
                                                                            </Box>
                                                                        </Box>

                                                                        <Box sx={{ mb: 1 }}>
                                                                            <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                                                                                Contact:
                                                                            </Typography>
                                                                            <Box sx={{ ml: 1 }}>
                                                                                <div>Phone: {change.new_data?.contact_telefoon || change.new_data?.phone || 'N/A'}</div>
                                                                                <div>Email: {change.new_data?.contact_emailadres || change.new_data?.email || 'N/A'}</div>
                                                                                <div>Website: {change.new_data?.contact_website || change.new_data?.website || 'N/A'}</div>
                                                                            </Box>
                                                                        </Box>

                                                                        <Box sx={{ mb: 1 }}>
                                                                            <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                                                                                Owner:
                                                                            </Typography>
                                                                            <Box sx={{ ml: 1 }}>
                                                                                <div>Name: {change.new_data?.naam_houder || change.new_data?.owner_name || 'N/A'}</div>
                                                                                <div>KvK: {change.new_data?.kvk_nummer_houder || change.new_data?.kvk_number || 'N/A'}</div>
                                                                                <div>Municipality: {change.new_data?.verantwoordelijke_gemeente || change.new_data?.municipality || 'N/A'}</div>
                                                                            </Box>
                                                                        </Box>

                                                                        <Divider sx={{ my: 1 }} />
                                                                        <details>
                                                                            <summary style={{ cursor: 'pointer', fontSize: '0.7rem' }}>
                                                                                View All CSV Fields ({Object.keys(change.new_data || {}).length})
                                                                            </summary>
                                                                            <pre style={{ fontSize: '0.6rem', margin: '8px 0', whiteSpace: 'pre-wrap' }}>
                                                                                {JSON.stringify(change.new_data, null, 2)}
                                                                            </pre>
                                                                        </details>
                                                                    </Box>
                                                                </Paper>
                                                            </Grid>
                                                        </Grid>
                                                    </Box>
                                                </Collapse>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </React.Fragment>
                            ))}
                        </TableBody>
                    </Table>

                    <TablePagination
                        component="div"
                        count={filteredFacilities.length}
                        page={page}
                        onPageChange={handleChangePage}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        rowsPerPageOptions={[5, 10, 25, 50, 100]}
                        showFirstButton
                        showLastButton
                    />
                </TableContainer>
            </Box>
        );
    };

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" component="h1" gutterBottom>
                CSV Upload Manager
            </Typography>
            <Typography variant="body1" color="text.secondary" gutterBottom>
                Upload CSV files from the LRK to compare and process facility data
            </Typography>

            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
                    <Tab label="Upload" />
                    <Tab label="Results" disabled={!currentImport} />
                    <Tab label="Prompt Editor" />
                    <Tab label="Inspection Reports" />
                </Tabs>
            </Box>

            {tabValue === 0 && (
                <Box>
                    <Card sx={{ mb: 2 }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                CSV File Management
                            </Typography>
                            <FormGroup row>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={viewMode === 'upload'}
                                            onChange={(e) => setViewMode(e.target.checked ? 'upload' : 'recent')}
                                        />
                                    }
                                    label={viewMode === 'upload' ? 'Upload New CSV' : 'View Recent Imports'}
                                />
                            </FormGroup>
                            <Typography variant="body2" color="text.secondary">
                                {viewMode === 'recent'
                                    ? 'Select from previously uploaded CSV files or switch to upload a new one.'
                                    : 'Upload a new CSV file with LRK facility data.'
                                }
                            </Typography>
                        </CardContent>
                    </Card>

                    {viewMode === 'recent' ? renderRecentImports() : renderUploadArea()}
                </Box>
            )}
            {tabValue === 1 && (
                <>
                    {renderFilterBar()}
                    {renderFacilityChanges()}
                </>
            )}
            {tabValue === 2 && (
                <Card>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            Kimi Prompt Editor
                        </Typography>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                            Edit the prompt template used by Kimi AI for analyzing childcare facilities.
                            This prompt includes web search instructions and JSON output formatting for detailed facility analysis.
                        </Typography>
                        <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 2 }}>
                            💡 <strong>Tip:</strong> Use variables like {'{facility_name}'}, {'{facility_location}'}, and {'{facility_type}'}
                            to make your prompt dynamic. Click the variable chips below to insert them automatically.
                        </Typography>

                        <TextField
                            fullWidth
                            multiline
                            rows={20}
                            value={promptText}
                            onChange={(e) => setPromptText(e.target.value)}
                            placeholder="Enter your Kimi analysis prompt here..."
                            sx={{ mb: 2 }}
                        />

                        <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" gutterBottom>
                                Available Variables:
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {availableVariables && Object.entries(availableVariables).map(([key, description]) => (
                                    <Tooltip key={key} title={description}>
                                        <Chip
                                            label={`{${key}}`}
                                            size="small"
                                            onClick={() => {
                                                setPromptText(prev => prev + `{${key}}`);
                                            }}
                                            clickable
                                        />
                                    </Tooltip>
                                ))}
                                {!availableVariables && (
                                    <Typography variant="body2" color="text.secondary">
                                        Loading variables...
                                    </Typography>
                                )}
                            </Box>
                        </Box>

                        <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                            <Button variant="outlined" onClick={loadPromptTemplates}>
                                Load Templates
                            </Button>
                            <Button
                                variant="contained"
                                onClick={saveCurrentPrompt}
                                disabled={savingPrompt}
                            >
                                {savingPrompt ? <CircularProgress size={20} /> : 'Save Prompt'}
                            </Button>
                            <Button
                                variant="outlined"
                                onClick={resetToDefaultPrompt}
                                disabled={promptText === ''}
                            >
                                Reset to Default
                            </Button>
                        </Box>
                    </CardContent>
                </Card>
            )}

            {tabValue === 3 && (
                <Box>
                    <Card>
                        <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography variant="h6">
                                    Inspection Report Exports
                                </Typography>
                                <Button
                                    variant="outlined"
                                    startIcon={<Refresh />}
                                    onClick={loadInspectionExports}
                                    disabled={loadingExports}
                                >
                                    Refresh
                                </Button>
                            </Box>

                            {loadingExports && inspectionExports.length === 0 ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                                    <CircularProgress />
                                </Box>
                            ) : inspectionExports.length === 0 ? (
                                <Alert severity="info">
                                    No inspection report exports yet. Select facilities and click "Download All Inspection Reports" to create an export.
                                </Alert>
                            ) : (
                                <TableContainer>
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Export ID</TableCell>
                                                <TableCell>Facilities</TableCell>
                                                <TableCell>Reports</TableCell>
                                                <TableCell>Status</TableCell>
                                                <TableCell>Progress</TableCell>
                                                <TableCell>Created</TableCell>
                                                <TableCell>File Size</TableCell>
                                                <TableCell>Actions</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {inspectionExports.map((exp) => (
                                                <TableRow key={exp.id}>
                                                    <TableCell>#{exp.id}</TableCell>
                                                    <TableCell>{exp.total_facilities}</TableCell>
                                                    <TableCell>{exp.total_reports || '-'}</TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            label={exp.status}
                                                            size="small"
                                                            color={
                                                                exp.status === 'completed' ? 'success' :
                                                                exp.status === 'failed' ? 'error' :
                                                                exp.status === 'processing' ? 'warning' :
                                                                'default'
                                                            }
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        {exp.status === 'processing' ? (
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                <CircularProgress size={20} variant="determinate" value={exp.progress || 0} />
                                                                <Typography variant="body2">{exp.progress || 0}%</Typography>
                                                            </Box>
                                                        ) : exp.status === 'completed' ? (
                                                            <CheckCircleIcon color="success" />
                                                        ) : exp.status === 'failed' ? (
                                                            <ErrorIcon color="error" />
                                                        ) : (
                                                            <Typography variant="body2">Pending</Typography>
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        {new Date(exp.requested_at).toLocaleString()}
                                                    </TableCell>
                                                    <TableCell>
                                                        {exp.file_size_bytes ? 
                                                            `${(exp.file_size_bytes / 1024 / 1024).toFixed(2)} MB` : 
                                                            '-'
                                                        }
                                                    </TableCell>
                                                    <TableCell>
                                                        {exp.status === 'completed' && exp.download_url && (
                                                            <Button
                                                                variant="contained"
                                                                size="small"
                                                                startIcon={<CloudUploadIcon />}
                                                                href={`${API_BASE}${exp.download_url}`}
                                                                download
                                                            >
                                                                Download
                                                            </Button>
                                                        )}
                                                        {exp.status === 'failed' && exp.error_message && (
                                                            <Tooltip title={exp.error_message}>
                                                                <ErrorIcon color="error" />
                                                            </Tooltip>
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </CardContent>
                    </Card>
                </Box>
            )}

            {/* Process Dialog */}
            <Dialog open={showProcessDialog} onClose={() => setShowProcessDialog(false)} maxWidth="md" fullWidth>
                <DialogTitle>Process Selected Facilities</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" gutterBottom>
                        You are about to process {selectedFacilities.length} facilities with the current prompt.
                    </Typography>

                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        📊 All CSV data fields will be available as variables in your Kimi prompt.
                    </Typography>

                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={forceReprocess}
                                onChange={(e) => setForceReprocess(e.target.checked)}
                            />
                        }
                        label="Force reprocess (even if already processed)"
                    />

                    <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                        Current Prompt Preview:
                    </Typography>
                    <Paper sx={{ p: 2, bgcolor: 'grey.50', maxHeight: 200, overflow: 'auto' }}>
                        <Typography variant="body2" component="pre">
                            {promptText.substring(0, 500)}...
                        </Typography>
                    </Paper>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowProcessDialog(false)}>Cancel</Button>
                    <Button onClick={startBatchProcessing} variant="contained" disabled={loading}>
                        {loading ? <CircularProgress size={20} /> : 'Start Processing'}
                    </Button>
                </DialogActions>
            </Dialog>

            {loading && (
                <Box sx={{ position: 'fixed', bottom: 16, right: 16 }}>
                    <Card sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <CircularProgress size={24} />
                            <Typography variant="body2">Processing...</Typography>
                        </Box>
                    </Card>
                </Box>
            )}

            {/* Analysis Results Dialog */}
            <Dialog open={showAnalysisDialog} onClose={() => setShowAnalysisDialog(false)} maxWidth="lg" fullWidth>
                <DialogTitle>
                    🤖 Kimi Analysis Results
                    {selectedAnalysisData && (
                        <Typography variant="subtitle2" color="text.secondary">
                            {selectedAnalysisData.facility.new_data?.actuele_naam_oko || selectedAnalysisData.facility.new_data?.name || 'N/A'}
                        </Typography>
                    )}
                </DialogTitle>
                <DialogContent>
                    {selectedAnalysisData && selectedAnalysisData.results && (() => {
                        // Check for both regular Kimi analysis and CSV Kimi analysis
                        const kimiAnalysis = selectedAnalysisData.results?.kimi_analysis;
                        const csvKimiAnalysis = selectedAnalysisData.results?.csv_kimi_analysis;
                        const analysisResult = kimiAnalysis || csvKimiAnalysis;
                        const analysisType = kimiAnalysis ? 'Regular Kimi' : 'CSV Kimi';

                        console.log('🔍 Modal Debug - Analysis Result:', analysisResult);
                        console.log('🔍 Modal Debug - Result Structure:', analysisResult?.result);

                        if (!analysisResult) {
                            return (
                                <Box>
                                    <Typography color="error">No analysis results found</Typography>
                                    {selectedAnalysisData.error && (
                                        <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                                            Error: {selectedAnalysisData.error}
                                        </Typography>
                                    )}
                                    <Typography variant="body2" sx={{ mt: 2 }}>
                                        Available results: {Object.keys(selectedAnalysisData.results || {}).join(', ') || 'None'}
                                    </Typography>
                                </Box>
                            );
                        }

                        // Additional safety check for malformed analysis result
                        if (!analysisResult?.result && !analysisResult?.analyzed_at) {
                            return (
                                <Box>
                                    <Typography color="warning.main">Analysis result has unexpected structure</Typography>
                                    <Typography variant="body2" sx={{ mt: 1 }}>
                                        Analysis Type: {analysisType}
                                    </Typography>
                                    <Typography variant="body2" sx={{ mt: 1 }}>
                                        Expected: .result or .analyzed_at properties
                                    </Typography>
                                    <Paper sx={{ p: 2, bgcolor: 'grey.100', mt: 2, maxHeight: 200, overflow: 'auto' }}>
                                        <Typography component="pre" variant="body2" style={{ fontSize: '0.75rem' }}>
                                            {JSON.stringify(analysisResult, null, 2)}
                                        </Typography>
                                    </Paper>
                                </Box>
                            );
                        }

                        return (
                            <Box>
                                <Typography variant="h6" gutterBottom>
                                    {analysisType} Analysis Summary
                                </Typography>
                                {analysisResult?.result?.confidence !== undefined && (
                                    <Box sx={{ mb: 3 }}>
                                        <Typography variant="subtitle2">Confidence Score:</Typography>
                                        <Typography variant="body1" color={
                                            analysisResult?.result?.confidence > 0.7 ? "success.main" :
                                                analysisResult?.result?.confidence > 0.4 ? "warning.main" : "error.main"
                                        }>
                                            {(analysisResult?.result?.confidence * 100).toFixed(1)}%
                                        </Typography>
                                    </Box>
                                )}

                                {analysisResult.result?.description_nl && (
                                    <Box sx={{ mb: 3 }}>
                                        <Typography variant="subtitle2" gutterBottom>Dutch Description:</Typography>
                                        <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                                            <Typography variant="body2" style={{ whiteSpace: 'pre-wrap' }}>
                                                {analysisResult?.result?.description_nl}
                                            </Typography>
                                        </Paper>
                                    </Box>
                                )}

                                {analysisResult.result?.description_en && (
                                    <Box sx={{ mb: 3 }}>
                                        <Typography variant="subtitle2" gutterBottom>English Description:</Typography>
                                        <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                                            <Typography variant="body2" style={{ whiteSpace: 'pre-wrap' }}>
                                                {analysisResult?.result?.description_en}
                                            </Typography>
                                        </Paper>
                                    </Box>
                                )}

                                {analysisResult?.result?.pricing_details && analysisResult?.result?.pricing_details.length > 0 && (
                                    <Box sx={{ mb: 3 }}>
                                        <Typography variant="subtitle2" gutterBottom>Pricing Details:</Typography>
                                        <Stack spacing={1}>
                                            {analysisResult?.result?.pricing_details?.map((pricing, idx) => (
                                                <Chip
                                                    key={idx}
                                                    label={`${pricing.type}: €${pricing.pricePerHour || 'N/A'} per hour`}
                                                    color="primary"
                                                    variant="outlined"
                                                />
                                            ))}
                                        </Stack>
                                    </Box>
                                )}

                                {analysisResult?.result?.activities && (
                                    <Box sx={{ mb: 3 }}>
                                        <Typography variant="subtitle2" gutterBottom>Activities:</Typography>
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                            {Object.entries(analysisResult?.result?.activities || {})
                                                .filter(([key, value]) => value === true)
                                                .map(([activity, _]) => (
                                                    <Chip
                                                        key={activity}
                                                        label={activity.replace(/_/g, ' ')}
                                                        color="success"
                                                        size="small"
                                                    />
                                                ))}
                                        </Box>
                                    </Box>
                                )}

                                {analysisResult?.result?.amenities && (
                                    <Box sx={{ mb: 3 }}>
                                        <Typography variant="subtitle2" gutterBottom>Amenities:</Typography>
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                            {Object.entries(analysisResult?.result?.amenities || {})
                                                .filter(([key, value]) => value === true)
                                                .map(([amenity, _]) => (
                                                    <Chip
                                                        key={amenity}
                                                        label={amenity.replace(/_/g, ' ')}
                                                        color="info"
                                                        size="small"
                                                    />
                                                ))}
                                        </Box>
                                    </Box>
                                )}
                            </Box>
                        );
                    })()}

                    {/* Raw Analysis Data Accordion */}
                    {selectedAnalysisData && selectedAnalysisData.results && (() => {
                        const kimiAnalysis = selectedAnalysisData.results.kimi_analysis;
                        const csvKimiAnalysis = selectedAnalysisData.results.csv_kimi_analysis;
                        const analysisResult = kimiAnalysis || csvKimiAnalysis;
                        const analysisType = kimiAnalysis ? 'Regular Kimi' : 'CSV Kimi';

                        if (!analysisResult) return null;

                        return (
                            <Accordion sx={{ mt: 3 }}>
                                <AccordionSummary
                                    expandIcon={<ExpandMoreIcon />}
                                    aria-controls="raw-analysis-content"
                                    id="raw-analysis-header"
                                >
                                    <Typography variant="h6">
                                        🔍 Raw {analysisType} Analysis Data (JSON)
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Typography variant="body2" color="text.secondary" gutterBottom>
                                        Complete JSON response from {analysisType} AI analysis:
                                    </Typography>
                                    <Paper sx={{ p: 2, bgcolor: 'grey.900', color: 'grey.100', maxHeight: 400, overflow: 'auto' }}>
                                        <Typography component="pre" variant="body2" sx={{
                                            fontSize: '0.75rem',
                                            lineHeight: 1.4,
                                            whiteSpace: 'pre-wrap',
                                            fontFamily: 'monospace'
                                        }}>
                                            {JSON.stringify(analysisResult?.result || analysisResult, null, 2)}
                                        </Typography>
                                    </Paper>

                                    {/* Show analysis metadata too */}
                                    <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                                        Analysis Metadata:
                                    </Typography>
                                    <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                                        <Typography variant="body2">
                                            <strong>Analyzed at:</strong> {analysisResult?.analyzed_at || 'N/A'}
                                        </Typography>
                                        <Typography variant="body2">
                                            <strong>Status:</strong> {analysisResult?.status || 'N/A'}
                                        </Typography>
                                        <Typography variant="body2">
                                            <strong>Facility LRK ID:</strong> {selectedAnalysisData.facility.lrk_id || 'N/A'}
                                        </Typography>
                                    </Paper>
                                </AccordionDetails>
                            </Accordion>
                        );
                    })()}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowAnalysisDialog(false)}>Close</Button>
                </DialogActions>
            </Dialog>

            {loading && (
                <Box sx={{ position: 'fixed', bottom: 16, right: 16 }}>
                    <Card sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <CircularProgress size={24} />
                            <Typography variant="body2">Processing...</Typography>
                        </Box>
                    </Card>
                </Box>
            )}

            {/* Field Selection Modal */}
            <FieldSelectionModal
                open={showFieldSelectionModal}
                onClose={() => setShowFieldSelectionModal(false)}
                fieldOptions={fieldOptions}
                fieldSelection={fieldSelection}
                setFieldSelection={setFieldSelection}
                onSend={sendToKiddieWithSelection}
                loading={sendToKiddieLoading}
                loadingFieldOptions={loadingFieldOptions}
                selectedCount={getProcessedSelectedCount()}
            />
        </Box>
    );
};

// Field Selection Modal Component
const FieldSelectionModal = ({
    open,
    onClose,
    fieldOptions,
    fieldSelection,
    setFieldSelection,
    onSend,
    loading,
    loadingFieldOptions,
    selectedCount
}) => {
    const [presets] = useState({
        'minimal': {
            label: 'Minimal (Required Only)',
            description: 'Only send required fields',
            selection: {
                basic_info: ['name', 'lrk_id'],
                descriptions: ['description_nl'],
                location: ['address_text'],
                contact: [],
                ages_and_features: [],
                operational: [],
                activities: false,
                amenities: false,
                media: { photos: { enabled: false }, reviews: { enabled: false } },
                regulatory: [],
                administrative: []
            }
        },
        'standard': {
            label: 'Standard',
            description: 'Default recommended fields',
            selection: null // Will use fieldOptions.defaults
        },
        'complete': {
            label: 'Complete',
            description: 'All available data',
            selection: {
                basic_info: ['name', 'website', 'email', 'lrk_id', 'type'],
                descriptions: ['description_nl', 'description_en'],
                location: ['address_text', 'location'],
                contact: ['phone', 'contact_person'],
                ages_and_features: ['min_age', 'max_age', 'preschool_education', 'outside_playground', 'biological_food', 'half_days_possible', 'have_app', 'spoken_languages'],
                operational: ['group_structure', 'opening_hours', 'pricing_details'],
                activities: true,
                amenities: true,
                media: {
                    photos: { enabled: true, limit: 20 },
                    reviews: { enabled: true, limit: 50, min_rating: 1, since_date: null }
                },
                regulatory: ['inspection_reports', 'owner_data'],
                administrative: ['responsible_municipality', 'capacity', 'register_date', 'kvk']
            }
        }
    });

    const handlePresetChange = (presetKey) => {
        const preset = presets[presetKey];
        if (preset.selection) {
            setFieldSelection(preset.selection);
        } else {
            // Use defaults for 'standard'
            setFieldSelection(fieldOptions?.defaults || {});
        }
    };

    const handleFieldToggle = (category, field) => {
        setFieldSelection(prev => {
            const newSelection = { ...prev };
            if (!newSelection[category]) {
                newSelection[category] = [];
            }

            if (Array.isArray(newSelection[category])) {
                if (newSelection[category].includes(field)) {
                    newSelection[category] = newSelection[category].filter(f => f !== field);
                } else {
                    newSelection[category] = [...newSelection[category], field];
                }
            }

            return newSelection;
        });
    };

    const handleCollectionToggle = (category, enabled) => {
        setFieldSelection(prev => ({
            ...prev,
            [category]: enabled
        }));
    };

    const handleMediaOptionChange = (mediaType, option, value) => {
        setFieldSelection(prev => ({
            ...prev,
            media: {
                ...prev.media,
                [mediaType]: {
                    ...prev.media?.[mediaType],
                    [option]: value
                }
            }
        }));
    };

    const handleSend = () => {
        onSend(fieldSelection);
    };

    const handleSendDefault = () => {
        onSend(null); // Send without field selection (use all fields)
    };

    if (loadingFieldOptions) {
        return (
            <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
                <DialogContent>
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <CircularProgress />
                    </Box>
                </DialogContent>
            </Dialog>
        );
    }

    if (!fieldOptions) {
        return null;
    }

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <DialogTitle>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h5">Send to Kiddie - Field Selection</Typography>
                    <Chip
                        label={`${selectedCount} facilities`}
                        color="primary"
                        size="small"
                    />
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Choose exactly which fields to send to the Kiddie API. You can use presets or customize individual fields.
                </Typography>
            </DialogTitle>

            <DialogContent dividers>
                {/* Presets */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" gutterBottom>Quick Presets</Typography>
                    <Grid container spacing={2}>
                        {Object.entries(presets).map(([key, preset]) => (
                            <Grid item xs={12} sm={4} key={key}>
                                <Card
                                    sx={{
                                        cursor: 'pointer',
                                        border: '2px solid transparent',
                                        '&:hover': { borderColor: 'primary.main' }
                                    }}
                                    onClick={() => handlePresetChange(key)}
                                >
                                    <CardContent sx={{ textAlign: 'center', py: 2 }}>
                                        <Typography variant="subtitle1" fontWeight="bold">
                                            {preset.label}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {preset.description}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                <Divider sx={{ my: 3 }} />

                {/* Field Categories */}
                <Typography variant="h6" gutterBottom>Customize Fields</Typography>

                {Object.entries(fieldOptions.categories).map(([categoryKey, category]) => (
                    <Accordion key={categoryKey} defaultExpanded={categoryKey === 'basic_info'}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                                <Typography variant="subtitle1" fontWeight="bold">
                                    {category.label}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                                    {category.description}
                                </Typography>
                                {/* Show selection count */}
                                <Chip
                                    size="small"
                                    label={getSelectionCount(categoryKey)}
                                    color={getSelectionCount(categoryKey) > 0 ? 'primary' : 'default'}
                                />
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                            {category.is_collection ? (
                                // Handle activities/amenities
                                <Box>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={fieldSelection?.[categoryKey] === true}
                                                onChange={(e) => handleCollectionToggle(categoryKey, e.target.checked)}
                                            />
                                        }
                                        label={`Include all ${category.label.toLowerCase()}`}
                                    />
                                    {category.available_items && (
                                        <Box sx={{ mt: 2 }}>
                                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                                Available items: {category.available_items.join(', ')}
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                            ) : category.fields ? (
                                // Handle regular field categories
                                <Grid container spacing={2}>
                                    {Object.entries(category.fields).map(([fieldKey, field]) => {
                                        if (fieldKey === 'photos' || fieldKey === 'reviews') {
                                            // Special handling for media fields
                                            return (
                                                <Grid item xs={12} key={fieldKey}>
                                                    <Card variant="outlined" sx={{ p: 2 }}>
                                                        <FormControlLabel
                                                            control={
                                                                <Switch
                                                                    checked={fieldSelection?.media?.[fieldKey]?.enabled || false}
                                                                    onChange={(e) => handleMediaOptionChange(fieldKey, 'enabled', e.target.checked)}
                                                                />
                                                            }
                                                            label={field.label}
                                                        />
                                                        {fieldSelection?.media?.[fieldKey]?.enabled && field.options && (
                                                            <Box sx={{ mt: 2, ml: 4 }}>
                                                                {Object.entries(field.options).map(([optionKey, option]) => (
                                                                    <Box key={optionKey} sx={{ mb: 2 }}>
                                                                        <Typography variant="body2" gutterBottom>
                                                                            {option.label}
                                                                        </Typography>
                                                                        {option.type === 'number' && (
                                                                            <TextField
                                                                                type="number"
                                                                                size="small"
                                                                                value={fieldSelection?.media?.[fieldKey]?.[optionKey] || option.default}
                                                                                onChange={(e) => handleMediaOptionChange(fieldKey, optionKey, parseInt(e.target.value))}
                                                                                inputProps={{ min: option.min, max: option.max }}
                                                                                sx={{ width: 120 }}
                                                                            />
                                                                        )}
                                                                        {option.type === 'date' && (
                                                                            <TextField
                                                                                type="date"
                                                                                size="small"
                                                                                value={fieldSelection?.media?.[fieldKey]?.[optionKey] || ''}
                                                                                onChange={(e) => handleMediaOptionChange(fieldKey, optionKey, e.target.value || null)}
                                                                                sx={{ width: 200 }}
                                                                            />
                                                                        )}
                                                                    </Box>
                                                                ))}
                                                            </Box>
                                                        )}
                                                    </Card>
                                                </Grid>
                                            );
                                        }

                                        return (
                                            <Grid item xs={12} sm={6} md={4} key={fieldKey}>
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={fieldSelection?.[categoryKey]?.includes?.(fieldKey) || false}
                                                            onChange={() => handleFieldToggle(categoryKey, fieldKey)}
                                                        />
                                                    }
                                                    label={
                                                        <Box>
                                                            <Typography variant="body2">
                                                                {field.label}
                                                                {field.required && <span style={{ color: 'red' }}> *</span>}
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary">
                                                                Source: {field.source}
                                                            </Typography>
                                                        </Box>
                                                    }
                                                />
                                            </Grid>
                                        );
                                    })}
                                </Grid>
                            ) : null}
                        </AccordionDetails>
                    </Accordion>
                ))}
            </DialogContent>

            <DialogActions sx={{ p: 3 }}>
                <Button onClick={onClose} disabled={loading}>
                    Cancel
                </Button>
                <Button
                    onClick={handleSendDefault}
                    disabled={loading}
                    variant="outlined"
                >
                    Send All Fields (Default)
                </Button>
                <Button
                    onClick={handleSend}
                    disabled={loading}
                    variant="contained"
                    startIcon={loading ? <CircularProgress size={20} /> : <UploadIcon />}
                >
                    {loading ? 'Sending...' : 'Send Selected Fields'}
                </Button>
            </DialogActions>
        </Dialog>
    );

    function getSelectionCount(categoryKey) {
        const selection = fieldSelection?.[categoryKey];
        if (Array.isArray(selection)) {
            return selection.length;
        } else if (selection === true) {
            return 'All';
        } else if (categoryKey === 'media') {
            const count = Object.values(selection || {}).filter(item => item?.enabled).length;
            return count;
        }
        return 0;
    }
};

export default CSVUploadManager; 